import { landingUrls } from 'config/urls';
import { RecentActivityDto, RecentActivityItem, UpdateActivityPayload } from 'types/landing';
import { getConfig } from 'utils/helperMethods';
import { isSafePostLoginPath } from 'utils/postLoginRedirect';
import { findScreenEntryByPath, LANDING_ROUTE } from 'utils/routeResolution';

export const ACTIVITY_SESSION_STORAGE_KEY = 'rgm_activity_session_id';

export type ActivityMeta = {
    page_id?: string;
    title: string;
    module_label: string;
    module_id?: string;
};

const SKIP_EXACT = new Set(['/', LANDING_ROUTE]);

export const DEFAULT_RECENT_ACTIVITY_LIMIT = 5;

export const resolveRecentActivityLimit = (appConfig: any): number => {
    const raw = getConfig('commonConfig', appConfig)?.recentActivityLimit;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1) return DEFAULT_RECENT_ACTIVITY_LIMIT;
    return Math.floor(n);
};

export const getStoredActivitySessionId = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACTIVITY_SESSION_STORAGE_KEY);
};

export const setStoredActivitySessionId = (sessionId: string): void => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(ACTIVITY_SESSION_STORAGE_KEY, sessionId);
};

export const isActivityPathSkipped = (pathname: string): boolean => {
    if (!pathname || SKIP_EXACT.has(pathname)) return true;
    if (pathname.startsWith('/login')) return true;
    if (pathname.startsWith('/callback')) return true;
    if (pathname.startsWith('/admin')) return true;
    return pathname.includes('ask-asper');
};

const readAccessScreens = (): string[] | undefined => {
    try {
        const scope = JSON.parse(localStorage.getItem('userAccessScope') || '{}');
        return scope?.data_metrics?.forecast_sources?.screens;
    } catch {
        return undefined;
    }
};

export const canOpenActivity = (pageId?: string): boolean => {
    if (!pageId) return true;
    const screens = readAccessScreens();
    if (!Array.isArray(screens) || screens.length === 0) return true;
    return screens.includes(pageId);
};

export const formatRelativeActivityTime = (iso: string, now = Date.now()): string => {
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return '';
    const diffMs = Math.max(0, now - then);
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
};

const findModuleForPage = (groups: any[], needle?: string): { module_label: string; module_id?: string } => {
    if (!needle || !Array.isArray(groups)) return { module_label: '' };
    for (const group of groups) {
        const options = group?.options || {};
        for (const key of Object.keys(options)) {
            const entries = options[key];
            if (!Array.isArray(entries)) continue;
            if (entries.some((entry: any) => entry?.id === needle)) {
                return { module_label: group.label || '', module_id: group.id };
            }
        }
    }
    return { module_label: '' };
};

const titleFromMatch = (match: any, details: any[]): string => {
    if (match?.label) return match.label;
    const parent = details.find((screen) =>
        screen?.subRoutes?.some((sub: any) => sub?.id === match?.id || sub?.pageId === match?.pageId),
    );
    return parent?.label || '';
};

export const resolveActivityMeta = (pathname: string, appConfig: any): ActivityMeta | null => {
    const details = getConfig('screenDetailsWithRoutes', appConfig) || [];
    const match = findScreenEntryByPath(details, pathname);
    if (!match) return null;
    const title = titleFromMatch(match, details);
    if (!title) return null;
    const pageId = match.pageId;
    const optionId = (match as { id?: string }).id;
    const { module_label, module_id } = findModuleForPage(getConfig('screens', appConfig) || [], optionId || pageId);
    if (!module_label) return null;
    const meta: ActivityMeta = { title, module_label };
    if (pageId) meta.page_id = pageId;
    if (module_id) meta.module_id = module_id;
    return meta;
};

export const toRecentActivityItem = (dto: RecentActivityDto): RecentActivityItem | null => {
    if (!dto?.route || !dto.title || !isSafePostLoginPath(dto.route)) return null;
    return {
        id: dto.activity_id,
        title: dto.title,
        moduleLabel: dto.module_label,
        timeLabel: formatRelativeActivityTime(dto.visited_at),
        route: dto.route,
        pageId: dto.page_id,
    };
};

export const buildUpdateActivityPayload = (
    sessionId: string,
    route: string,
    meta: ActivityMeta,
): UpdateActivityPayload => ({
    session_id: sessionId,
    route,
    title: meta.title,
    module_label: meta.module_label,
    ...(meta.page_id ? { page_id: meta.page_id } : {}),
    ...(meta.module_id ? { module_id: meta.module_id } : {}),
});

export const ensureActivitySessionId = async (accessToken: string, forceNew: boolean): Promise<string | null> => {
    if (!forceNew) {
        const existing = getStoredActivitySessionId();
        if (existing) return existing;
    }
    try {
        const response = await fetch(landingUrls.getSessionIdUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: '{}',
        });
        if (!response.ok) return getStoredActivitySessionId();
        const data = await response.json();
        const sessionId = data?.session_id;
        if (typeof sessionId !== 'string' || !sessionId) return null;
        setStoredActivitySessionId(sessionId);
        return sessionId;
    } catch {
        return getStoredActivitySessionId();
    }
};
